import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, BarChart3, RotateCcw, Loader2, School, Calendar,
  Settings, Save, Calculator, Shield, AlertTriangle, Award,
  BookOpen, Users, Clock, Ban, Percent, FileText, Info, CheckCircle2,
  XCircle, Lightbulb
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== API RESPONSE HELPER ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.results)) return res.results;
  }
  return [];
};

const getErrorDetail = (err: any, fallback: string = ''): string => {
  const data = (err as any)?.response?.data as any;
  return data?.detail || data?.message || err?.message || fallback;
};

const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) {
        return Number(state.user.school_id)
      }
    }
  } catch (e) {
    console.warn('Failed to get school_id from auth-storage:', e)
  }
  return null
}

const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yearId) return Number(yearId)
  } catch (e) {
    console.warn('Failed to get year_id:', e)
  }
  return null
}

const loadWithFallback = async (
  url: string,
  params: Record<string, any>,
  isPrivileged: boolean
): Promise<any[]> => {
  try {
    const res = await apiService.get(url, { params }).catch(() => ({ data: [] }));
    let data = extractData(res);
    if (data.length === 0 && params.school_id && isPrivileged) {
      const { school_id, ...paramsNoSchool } = params;
      const res2 = await apiService.get(url, { params: paramsNoSchool }).catch(() => ({ data: [] }));
      data = extractData(res2);
      if (data.length > 0) {
        console.warn(`[${url}] Fallback without school_id returned ${data.length} items`);
      }
    }
    return data;
  } catch (e) {
    console.warn(`[${url}] Load error:`, e);
    return [];
  }
};

const getAppreciationBadgeColor = (value: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) return 'bg-gray-100 text-gray-500';
  if (value >= 16) return 'bg-emerald-100 text-emerald-700';
  if (value >= 14) return 'bg-blue-100 text-blue-700';
  if (value >= 12) return 'bg-cyan-100 text-cyan-700';
  if (value >= 10) return 'bg-gray-100 text-gray-700';
  if (value >= 8) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};

// ==================== TYPES ====================
interface GradeSettings {
  id: number;
  school_id: number;
  academic_year_id: number;
  trimester_formula: string;
  annual_formula: string;
  ignore_empty_subjects: boolean;
  warning_conduct_absence_hours: number;
  warning_conduct_exclusion_days: number;
  blame_conduct_absence_hours: number;
  blame_conduct_exclusion_days: number;
  blame_work_avg_threshold: number;
  warning_work_avg_threshold: number;
  honor_board_compo_min: number;
  honor_board_annual_min: number;
  encouragement_min: number;
  felicitations_min: number;
  honor_refuse_absence_hours: number;
  honor_refuse_exclusion_days: number;
  honor_refuse_detention_hours: number;
  honor_refuse_blame_count: number;
  min_subjects_percentage: number;
  min_coefficients_percentage: number;
}

export default function GradesAveragePage() {
  const { roleName, isAdmin, isSuperAdmin } = useAuthStore();
  const { lang } = useLang();

  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId)
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId)
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null)

  const t = {
    fr: {
      title: 'Moyennes',
      subtitle: 'Moyennes générales et par matière',
      generalAverage: 'Moyenne Générale',
      subjectAverages: 'Moyennes par Matière',
      subject: 'Matière',
      coefficient: 'Coefficient',
      avg: 'Moy',
      students: 'Élèves',
      min: 'Min',
      max: 'Max',
      noData: 'Aucune donnée',
      selectClass: 'Sélectionnez une classe et une séquence',
      loading: 'Chargement...',
      refresh: 'Rafraîchir',
      error: 'Erreur',
      noYearSelected: 'Aucune année scolaire sélectionnée',
      yearRequired: 'Veuillez sélectionner une année scolaire dans le header',
      tabAverages: 'Moyennes',
      tabFormulas: 'Formules de calcul',
      formulasTitle: 'Paramètres de calcul',
      ignoreEmptySubjects: 'Ignorer les matières sans notes',
      ignoreEmptyDesc: 'Les matières sans notes ne seront pas prises en compte dans la moyenne générale.',
      trimesterFormula: 'Formule trimestrielle / semestrielle',
      annualFormula: 'Formule annuelle / finale',
      formulaWeighted: 'Moyenne pondérée des notes',
      formulaArithmetic: 'Moyenne arithmétique',
      conductTitle: 'Conduite',
      warningConduct: 'Avertissement conduite',
      blameConduct: 'Blâme conduite',
      whenAbsenceHours: 'Heures absence >=',
      whenExclusionDays: 'Jours exclusion >=',
      workTitle: 'Travail',
      warningWork: 'Avertissement travail',
      blameWork: 'Blâme travail',
      whenAvgBelow: 'Moyenne /20 <',
      honorTitle: "Tableau d'honneur",
      honorConditions: "Conditions d'accord",
      honorRefuse: 'Conditions de refus',
      honorCompoMin: 'Moy. Compo /20 >=',
      honorAnnualMin: 'Moy. Annuel /20 >=',
      encouragementMin: 'Encouragements /20 >=',
      felicitationsMin: 'Félicitations /20 >=',
      refuseAbsence: 'Heures absence >=',
      refuseExclusion: 'Jours exclusion >=',
      refuseDetention: 'Heures consigne >=',
      refuseBlames: 'Nbre blâmes >=',
      rankingTitle: 'Classement',
      minSubjectsPercent: '% matières composées <',
      minCoefsPercent: '% coefficients composés <',
      save: 'Enregistrer',
      saving: 'Enregistrement...',
      saveSuccess: 'Paramètres enregistrés',
      accessDenied: 'Accès réservé aux administrateurs',
      // NEW: Formules explicites
      calcPrinciple: 'Principe de calcul',
      calcPrincipleDesc: 'Ces règles sont appliquées lors de la génération des bulletins et des PV de conseil de classe.',
      sequenceCalc: 'Calcul d\'une séquence',
      sequenceCalcDesc: 'Pour chaque matière : Note × Coefficient = Points. La moyenne = Total des points ÷ Total des coefficients (EPS sans note est ignorée).',
      trimesterCalc: 'Calcul du trimestre / semestre',
      trimesterRecommended: 'Recalcul matière par matière (recommandé)',
      trimesterRecommendedDesc: "Pour chaque matière, on fait la moyenne de ses notes de séquences, puis on applique le coefficient. C'est la méthode officielle pour les bulletins.",
      trimesterNotRecommended: 'Moyenne arithmétique des moyennes de séquences',
      trimesterNotRecommendedDesc: 'On fait simplement la moyenne des moyennes de séquence. Cette méthode est moins précise et déconseillée pour les bulletins.',
      annualCalc: 'Calcul annuel / final',
      annualRecommended: 'Recalcul matière par matière à partir des trimestres (recommandé)',
      annualRecommendedDesc: 'Pour chaque matière, on fait la moyenne de ses notes trimestrielles, puis on applique le coefficient. Méthode officielle.',
      annualTrimesters: 'Moyenne arithmétique des moyennes trimestrielles',
      annualTrimestersDesc: 'On fait la moyenne des moyennes trimestrielles. Déconseillée car elle ne reflète pas le poids réel des coefficients.',
      annualSequences: 'Moyenne arithmétique des moyennes de séquences',
      annualSequencesDesc: "On fait la moyenne de toutes les moyennes de séquence de l'année. Déconseillée.",
      warningNotRecommended: 'Attention : cette méthode ne respecte pas le poids des coefficients et peut fausser le classement des élèves.',
      exampleTitle: 'Exemple de calcul trimestriel',
      exampleStep1: 'Séquence 1 : Maths 14×4 + Français 12×3 = 92 pts / 7 coefs → 13,14',
      exampleStep2: 'Séquence 2 : Maths 16×4 + Français 14×3 = 106 pts / 7 coefs → 15,14',
      exampleStep3: 'Trimestre (recommandé) : Maths (14+16)/2=15×4=60 + Français (12+14)/2=13×3=39 → 99/7 = 14,14',
      exampleStep4: 'Trimestre (non recommandé) : (13,14 + 15,14) / 2 = 14,14 → même résultat ici, mais pas toujours !',
      exampleNote: 'La différence apparaît quand un élève a des coefficients très déséquilibrés entre les séquences.',
      methodRecommended: 'Recommandé pour les bulletins',
      methodNotRecommended: 'Non recommandé',
    },
    en: {
      title: 'Averages',
      subtitle: 'General and subject averages',
      generalAverage: 'General Average',
      subjectAverages: 'Subject Averages',
      subject: 'Subject',
      coefficient: 'Coefficient',
      avg: 'Avg',
      students: 'Students',
      min: 'Min',
      max: 'Max',
      noData: 'No data',
      selectClass: 'Select a class and sequence',
      loading: 'Loading...',
      refresh: 'Refresh',
      error: 'Error',
      noYearSelected: 'No academic year selected',
      yearRequired: 'Please select an academic year in the header',
      tabAverages: 'Averages',
      tabFormulas: 'Calculation Formulas',
      formulasTitle: 'Calculation Settings',
      ignoreEmptySubjects: 'Ignore subjects without grades',
      ignoreEmptyDesc: 'Subjects without grades will not be included in the general average.',
      trimesterFormula: 'Trimester / Semester formula',
      annualFormula: 'Annual / Final formula',
      formulaWeighted: 'Weighted average of grades',
      formulaArithmetic: 'Arithmetic average',
      conductTitle: 'Conduct',
      warningConduct: 'Warning conduct',
      blameConduct: 'Blame conduct',
      whenAbsenceHours: 'Absence hours >=',
      whenExclusionDays: 'Exclusion days >=',
      workTitle: 'Work',
      warningWork: 'Warning work',
      blameWork: 'Blame work',
      whenAvgBelow: 'Average /20 <',
      honorTitle: 'Honor Board',
      honorConditions: 'Grant conditions',
      honorRefuse: 'Refuse conditions',
      honorCompoMin: 'Compo avg /20 >=',
      honorAnnualMin: 'Annual avg /20 >=',
      encouragementMin: 'Encouragements /20 >=',
      felicitationsMin: 'Congratulations /20 >=',
      refuseAbsence: 'Absence hours >=',
      refuseExclusion: 'Exclusion days >=',
      refuseDetention: 'Detention hours >=',
      refuseBlames: 'Blame count >=',
      rankingTitle: 'Ranking',
      minSubjectsPercent: '% subjects taken <',
      minCoefsPercent: '% coefficients taken <',
      save: 'Save',
      saving: 'Saving...',
      saveSuccess: 'Settings saved',
      accessDenied: 'Access reserved for administrators',
      // NEW
      calcPrinciple: 'Calculation Principle',
      calcPrincipleDesc: 'These rules are applied when generating report cards and class council minutes.',
      sequenceCalc: 'Sequence calculation',
      sequenceCalcDesc: 'For each subject: Grade × Coefficient = Points. Average = Total points ÷ Total coefficients (PE without grade is ignored).',
      trimesterCalc: 'Trimester / Semester calculation',
      trimesterRecommended: 'Recalculate subject by subject (recommended)',
      trimesterRecommendedDesc: 'For each subject, average its sequence grades, then apply the coefficient. This is the official method for report cards.',
      trimesterNotRecommended: 'Arithmetic average of sequence averages',
      trimesterNotRecommendedDesc: 'Simply average the sequence averages. Less accurate and not recommended for report cards.',
      annualCalc: 'Annual / Final calculation',
      annualRecommended: 'Recalculate subject by subject from trimesters (recommended)',
      annualRecommendedDesc: 'For each subject, average its trimester grades, then apply the coefficient. Official method.',
      annualTrimesters: 'Arithmetic average of trimester averages',
      annualTrimestersDesc: 'Average the trimester averages. Not recommended as it does not reflect the real weight of coefficients.',
      annualSequences: 'Arithmetic average of sequence averages',
      annualSequencesDesc: 'Average all sequence averages of the year. Not recommended.',
      warningNotRecommended: 'Warning: this method does not respect coefficient weights and may distort student rankings.',
      exampleTitle: 'Trimester calculation example',
      exampleStep1: 'Sequence 1: Math 14×4 + French 12×3 = 92 pts / 7 coefs → 13.14',
      exampleStep2: 'Sequence 2: Math 16×4 + French 14×3 = 106 pts / 7 coefs → 15.14',
      exampleStep3: 'Trimester (recommended): Math (14+16)/2=15×4=60 + French (12+14)/2=13×3=39 → 99/7 = 14.14',
      exampleStep4: 'Trimester (not recommended): (13.14 + 15.14) / 2 = 14.14 → same result here, but not always!',
      exampleNote: 'The difference appears when a student has very unbalanced coefficients between sequences.',
      methodRecommended: 'Recommended for report cards',
      methodNotRecommended: 'Not recommended',
    }
  }[lang];

  const [activeTab, setActiveTab] = useState<'averages' | 'formulas'>('averages');
  const [classAverage, setClassAverage] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedSequence, setSelectedSequence] = useState<string>("");
  const [classes, setClasses] = useState<any[]>([]);
  const [sequences, setSequences] = useState<any[]>([]);
  const [settings, setSettings] = useState<GradeSettings | null>(null);
  const [settingsForm, setSettingsForm] = useState<Partial<GradeSettings>>({});
  const [selectedClassSchoolId, setSelectedClassSchoolId] = useState<number | null>(null);

  const canAccessFormulas = isAdmin || isSuperAdmin || roleName === 'proviseur';

  const schoolIdRef = React.useRef<number | null>(schoolId)
  const yearIdRef = React.useRef<number | null>(selectedAcademicYearId)

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = selectedAcademicYearId }, [selectedAcademicYearId])

  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {}
    if (schoolId) params.school_id = schoolId
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId
    return params
  }, [schoolId, selectedAcademicYearId])

  const getClassApiParams = useCallback(() => {
    const params: Record<string, any> = {}
    const effectiveSchoolId = selectedClassSchoolId || schoolId
    if (effectiveSchoolId) params.school_id = effectiveSchoolId
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId
    return params
  }, [selectedClassSchoolId, schoolId, selectedAcademicYearId])

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const customEvent = e as CustomEvent
      const detail = customEvent.detail
      if (!detail?.year_id) return

      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null

      if (newYearId && newYearId === yearIdRef.current && (newSchoolId === null || newSchoolId === schoolIdRef.current)) {
        return
      }

      if (newYearId && newYearId !== yearIdRef.current) {
        setSelectedAcademicYearId(newYearId)
        const stored = localStorage.getItem(SELECTED_YEAR_ID_KEY)
        if (stored !== String(detail.year_id)) {
          try { localStorage.setItem(SELECTED_YEAR_ID_KEY, detail.year_id.toString()) } catch (err) { /* ignore */ }
        }
        if (detail.label) {
          const storedLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
          try { if (storedLabel !== detail.label) localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detail.label) } catch (err) { /* ignore */ }
          setCurrentAcademicYear({ id: newYearId, name: detail.label })
        }
      }

      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== schoolIdRef.current) {
        setSchoolId(newSchoolId)
        try { localStorage.setItem('lybibal-selected-school-id', newSchoolId.toString()); } catch (err) { /* ignore */ }
      }

      setSelectedClass("")
      setSelectedSequence("")
      setClassAverage(null)

      toast.info(lang === 'fr' ? 'Année scolaire changée - rechargement des données' : 'Academic year changed - reloading data')
    }

    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        if (newYearId && newYearId !== yearIdRef.current) {
          setSelectedAcademicYearId(newYearId)
          setSelectedClass("")
          setSelectedSequence("")
          setClassAverage(null)
        }
      }
    }
    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [lang])

  useEffect(() => {
    const yearId = getSelectedYearId()
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
    if (yearId) {
      setSelectedAcademicYearId(yearId)
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel })
    }

    try {
      const storedSchool = localStorage.getItem('lybibal-selected-school-id')
      if (storedSchool) {
        const sid = Number(storedSchool)
        if (!isNaN(sid)) setSchoolId(sid)
      }
    } catch (err) {
      // ignore
    }
  }, [])

  useEffect(() => {
    const loadFilters = async () => {
      if (!selectedAcademicYearId) {
        setClasses([])
        setSequences([])
        return
      }

      try {
        const filterParams = getFilterParams()
        const isPrivileged = isAdmin || isSuperAdmin || roleName === 'proviseur' || roleName === 'censeur';

        const [classesData, seqsData] = await Promise.all([
          loadWithFallback('/classes', filterParams, isPrivileged),
          loadWithFallback('/grades/sequences', filterParams, isPrivileged),
        ]);

        const filteredSeqs = Array.isArray(seqsData) && selectedAcademicYearId
          ? seqsData.filter((s: any) => {
              const matchesYear = (s.academic_year_id === undefined) ? true : Number(s.academic_year_id) === Number(selectedAcademicYearId);
              const matchesSchool = (s.school_id === undefined || !schoolId) ? true : Number(s.school_id) === Number(schoolId);
              return matchesYear && matchesSchool;
            })
          : (Array.isArray(seqsData) ? seqsData : [])

        setClasses(Array.isArray(classesData) ? classesData : []);
        setSequences(filteredSeqs);
      } catch (e) { 
        console.error('[GradesAveragePage] loadFilters error:', e); 
        setClasses([])
        setSequences([])
      }
    };
    loadFilters();
  }, [schoolId, selectedAcademicYearId, getFilterParams, isAdmin, isSuperAdmin, roleName]);

  const loadSettings = useCallback(async () => {
    if (!canAccessFormulas || !selectedAcademicYearId) return;
    try {
      const filterParams = getClassApiParams();
      const isPrivileged = isAdmin || isSuperAdmin || roleName === 'proviseur' || roleName === 'censeur';
      let res = await apiService.get('/grade-settings', { params: filterParams }).catch(() => null);
      let data = (res as any)?.data ?? res;
      if ((!data || (Array.isArray(data) && data.length === 0)) && filterParams.school_id && isPrivileged) {
        const { school_id, ...paramsNoSchool } = filterParams;
        res = await apiService.get('/grade-settings', { params: paramsNoSchool }).catch(() => null);
        data = (res as any)?.data ?? res;
      }
      if (data && typeof data === 'object') {
        setSettings(data);
        setSettingsForm(data);
      }
    } catch (err) {
      console.error('[GradesAveragePage] loadSettings error:', err);
    }
  }, [canAccessFormulas, schoolId, selectedAcademicYearId, getClassApiParams, isAdmin, isSuperAdmin, roleName]);

  useEffect(() => {
    if (activeTab === 'formulas' && canAccessFormulas) {
      loadSettings();
    }
  }, [activeTab, canAccessFormulas, loadSettings]);

  const loadClassAverage = useCallback(async () => {
    if (!selectedClass || !selectedAcademicYearId) return;

    setLoading(true);
    try {
      const filterParams = getClassApiParams()
      const params: any = {
        ...filterParams,
        sequence_id: selectedSequence ? parseInt(selectedSequence) : undefined,
      }

      const isPrivileged = isAdmin || isSuperAdmin || roleName === 'proviseur' || roleName === 'censeur';
      let res = await apiService.get(`/grades/averages/${selectedClass}`, { params }).catch(() => ({ data: null }));
      let data = (res as any)?.data ?? res;
      if ((!data || (Array.isArray(data) && data.length === 0)) && params.school_id && isPrivileged) {
        const { school_id, ...paramsNoSchool } = params;
        res = await apiService.get(`/grades/averages/${selectedClass}`, { params: paramsNoSchool }).catch(() => ({ data: null }));
        data = (res as any)?.data ?? res;
      }
      setClassAverage(data || null);
    } catch (err) {
      console.error(err);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedSequence, schoolId, selectedAcademicYearId, getClassApiParams, t.error, isAdmin, isSuperAdmin, roleName]);

  useEffect(() => {
    if (selectedClass && schoolId && selectedAcademicYearId) {
      loadClassAverage();
    }
  }, [loadClassAverage]);

  const handleSaveSettings = async () => {
    if (!canAccessFormulas) {
      toast.error(t.accessDenied);
      return;
    }
    setSavingSettings(true);
    try {
      const filterParams = getClassApiParams();
      await apiService.put('/grade-settings', settingsForm, { params: filterParams });
      toast.success(t.saveSuccess);
      loadSettings();
    } catch (err: any) {
      toast.error(getErrorDetail(err, t.error));
    } finally {
      setSavingSettings(false);
    }
  };

  const updateFormField = (field: keyof GradeSettings, value: any) => {
    setSettingsForm(prev => ({ ...prev, [field]: value }));
  };

  // Helper pour savoir si une formule est recommandée
  const isTrimesterRecommended = settingsForm.trimester_formula === 'weighted_sequences';
  const isAnnualRecommended = settingsForm.annual_formula === 'weighted_annual';

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {currentAcademicYear.name}
            </span>
            <span className="text-xs text-gray-400">
              school_id: {schoolId || "-"}
            </span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('averages')}
          className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'averages'
              ? 'bg-[#1e3a8a] text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          {t.tabAverages}
        </button>
        {canAccessFormulas && (
          <button
            onClick={() => setActiveTab('formulas')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
              activeTab === 'formulas'
                ? 'bg-[#1e3a8a] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Settings className="w-4 h-4" />
            {t.tabFormulas}
          </button>
        )}
      </div>

      {activeTab === 'averages' ? (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-3">
            <select
              value={selectedClass}
              onChange={(e) => {
                const cid = e.target.value
                setSelectedClass(cid)
                setClassAverage(null)
                const cls = classes.find((c: any) => String(c.id) === cid)
                setSelectedClassSchoolId(cls?.school_id ? Number(cls.school_id) : null)
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] min-w-[180px]"
            >
              <option value="">{lang === 'fr' ? 'Sélectionner une classe' : 'Select a class'}</option>
              {classes.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select
              value={selectedSequence}
              onChange={(e) => {
                setSelectedSequence(e.target.value)
                setClassAverage(null)
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] min-w-[180px]"
            >
              <option value="">{lang === 'fr' ? 'Séquence (optionnel)' : 'Sequence (optional)'}</option>
              {sequences.map((s: any) => (
                <option key={s.id} value={s.id}>{s.label || s.name}</option>
              ))}
            </select>
            <button
              onClick={loadClassAverage}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
              title={t.refresh}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">{t.loading}</span>
            </div>
          ) : !selectedClass ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">{t.selectClass}</p>
            </div>
          ) : !classAverage ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">{lang === 'fr' ? 'Aucune moyenne disponible pour cette classe' : 'No average available for this class'}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* General Average */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{t.generalAverage}</h3>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-[#1e3a8a]">{(classAverage.general_average ?? 0).toFixed(2)}</p>
                    <p className="text-sm text-gray-500">/ 20</p>
                  </div>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={classAverage.subject_averages || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="subject_name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis domain={[0, 20]} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value: number) => [value.toFixed(2), 'Moyenne']} />
                      <Bar dataKey="average" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Subject Averages Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#1e3a8a]" />
                    {t.subjectAverages}
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t.subject}</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t.coefficient}</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t.avg}</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t.students}</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t.min}</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t.max}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {classAverage.subject_averages?.map((subj: any) => (
                        <tr key={subj.subject_id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{subj.subject_name}</td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700">{subj.coefficient}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold ${getAppreciationBadgeColor(subj.average)}`}>
                              {(subj.average ?? 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700">{subj.students_count}</td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700">{subj.min?.toFixed(2) ?? '-'}</td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700">{subj.max?.toFixed(2) ?? '-'}</td>
                        </tr>
                      )) || (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">{t.noData}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ==================== ONGLET FORMULES ==================== */
        <div className="space-y-6">
          {!canAccessFormulas ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">{t.accessDenied}</p>
            </div>
          ) : !settings ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">{t.loading}</span>
            </div>
          ) : (
            <>
              {/* 🆕 CARTE PRINCIPE DE CALCUL */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                    <Lightbulb className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-blue-900 mb-1">{t.calcPrinciple}</h3>
                    <p className="text-sm text-blue-700 mb-4">{t.calcPrincipleDesc}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Séquence */}
                      <div className="bg-white/70 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">1</span>
                          <h4 className="text-sm font-bold text-gray-900">{t.sequenceCalc}</h4>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{t.sequenceCalcDesc}</p>
                      </div>

                      {/* Trimestre */}
                      <div className="bg-white/70 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">2</span>
                          <h4 className="text-sm font-bold text-gray-900">{t.trimesterCalc}</h4>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {lang === 'fr'
                            ? 'Pour chaque matière, moyenne des notes de séquences → × coefficient → total points ÷ total coefs.'
                            : 'For each subject, average its sequence grades → × coefficient → total points ÷ total coefs.'}
                        </p>
                      </div>

                      {/* Annuel */}
                      <div className="bg-white/70 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                          <h4 className="text-sm font-bold text-gray-900">{t.annualCalc}</h4>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {lang === 'fr'
                            ? 'Pour chaque matière, moyenne des notes trimestrielles → × coefficient → total points ÷ total coefs.'
                            : 'For each subject, average its trimester grades → × coefficient → total points ÷ total coefs.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 🆕 EXEMPLE VISUEL */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
                  <Info className="w-4 h-4 text-blue-600" />
                  {t.exampleTitle}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <span className="text-blue-600 font-bold shrink-0">S1</span>
                      <p className="text-gray-700">{t.exampleStep1}</p>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <span className="text-blue-600 font-bold shrink-0">S2</span>
                      <p className="text-gray-700">{t.exampleStep2}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <p className="text-gray-700">{t.exampleStep3}</p>
                    </div>
                    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <XCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-gray-700">{t.exampleStep4}</p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3 italic">{t.exampleNote}</p>
              </div>

              {/* Formules de calcul */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Calculator className="w-5 h-5 text-[#1e3a8a]" />
                  {t.formulasTitle}
                </h3>

                {/* Ignorer les matières sans notes */}
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settingsForm.ignore_empty_subjects ?? false}
                      onChange={(e) => updateFormField('ignore_empty_subjects', e.target.checked)}
                      className="w-5 h-5 text-[#1e3a8a] rounded border-gray-300 focus:ring-[#1e3a8a]"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {lang === 'fr' ? 'Ignorer les matières sans notes dans le calcul' : 'Ignore subjects without grades in calculation'}
                      </span>
                      <p className="text-xs text-gray-500">
                        {lang === 'fr'
                          ? 'Si coché, les matières sans notes ne seront pas prises en compte dans la moyenne générale.'
                          : 'If checked, subjects without grades will not be included in the general average.'}
                      </p>
                    </div>
                  </label>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Formule trimestrielle */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-[#1e3a8a] text-white text-xs font-bold flex items-center justify-center">T</span>
                      <label className="text-sm font-bold text-gray-900">{t.trimesterCalc}</label>
                    </div>

                    {/* Option recommandée */}
                    <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      settingsForm.trimester_formula === 'weighted_sequences'
                        ? 'border-emerald-500 bg-emerald-50/50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="trimester_formula"
                        checked={settingsForm.trimester_formula === 'weighted_sequences'}
                        onChange={() => updateFormField('trimester_formula', 'weighted_sequences')}
                        className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{t.trimesterRecommended}</span>
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full uppercase tracking-wide">
                            {t.methodRecommended}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{t.trimesterRecommendedDesc}</p>
                      </div>
                    </label>

                    {/* Option non recommandée */}
                    <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      settingsForm.trimester_formula === 'arithmetic_sequences'
                        ? 'border-amber-400 bg-amber-50/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="trimester_formula"
                        checked={settingsForm.trimester_formula === 'arithmetic_sequences'}
                        onChange={() => updateFormField('trimester_formula', 'arithmetic_sequences')}
                        className="w-4 h-4 text-amber-600 mt-0.5 shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{t.trimesterNotRecommended}</span>
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wide">
                            {t.methodNotRecommended}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{t.trimesterNotRecommendedDesc}</p>
                      </div>
                    </label>

                    {!isTrimesterRecommended && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">{t.warningNotRecommended}</p>
                      </div>
                    )}
                  </div>

                  {/* Formule annuelle */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-[#1e3a8a] text-white text-xs font-bold flex items-center justify-center">A</span>
                      <label className="text-sm font-bold text-gray-900">{t.annualCalc}</label>
                    </div>

                    {/* Option recommandée */}
                    <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      settingsForm.annual_formula === 'weighted_annual'
                        ? 'border-emerald-500 bg-emerald-50/50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="annual_formula"
                        checked={settingsForm.annual_formula === 'weighted_annual'}
                        onChange={() => updateFormField('annual_formula', 'weighted_annual')}
                        className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{t.annualRecommended}</span>
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full uppercase tracking-wide">
                            {t.methodRecommended}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{t.annualRecommendedDesc}</p>
                      </div>
                    </label>

                    {/* Option non recommandée 1 */}
                    <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      settingsForm.annual_formula === 'arithmetic_trimesters'
                        ? 'border-amber-400 bg-amber-50/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="annual_formula"
                        checked={settingsForm.annual_formula === 'arithmetic_trimesters'}
                        onChange={() => updateFormField('annual_formula', 'arithmetic_trimesters')}
                        className="w-4 h-4 text-amber-600 mt-0.5 shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{t.annualTrimesters}</span>
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wide">
                            {t.methodNotRecommended}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{t.annualTrimestersDesc}</p>
                      </div>
                    </label>

                    {/* Option non recommandée 2 */}
                    <label className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      settingsForm.annual_formula === 'arithmetic_sequences'
                        ? 'border-amber-400 bg-amber-50/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="annual_formula"
                        checked={settingsForm.annual_formula === 'arithmetic_sequences'}
                        onChange={() => updateFormField('annual_formula', 'arithmetic_sequences')}
                        className="w-4 h-4 text-amber-600 mt-0.5 shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{t.annualSequences}</span>
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wide">
                            {t.methodNotRecommended}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{t.annualSequencesDesc}</p>
                      </div>
                    </label>

                    {!isAnnualRecommended && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800">{t.warningNotRecommended}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Conduite */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  {t.conductTitle}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-amber-800 mb-3">{t.warningConduct}</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-amber-700 mb-1">{t.whenAbsenceHours}</label>
                        <input
                          type="number"
                          min={0}
                          value={settingsForm.warning_conduct_absence_hours ?? 8}
                          onChange={(e) => updateFormField('warning_conduct_absence_hours', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-amber-700 mb-1">{t.whenExclusionDays}</label>
                        <input
                          type="number"
                          min={0}
                          value={settingsForm.warning_conduct_exclusion_days ?? 5}
                          onChange={(e) => updateFormField('warning_conduct_exclusion_days', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-red-800 mb-3">{t.blameConduct}</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-red-700 mb-1">{t.whenAbsenceHours}</label>
                        <input
                          type="number"
                          min={0}
                          value={settingsForm.blame_conduct_absence_hours ?? 8}
                          onChange={(e) => updateFormField('blame_conduct_absence_hours', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-red-700 mb-1">{t.whenExclusionDays}</label>
                        <input
                          type="number"
                          min={0}
                          value={settingsForm.blame_conduct_exclusion_days ?? 12}
                          onChange={(e) => updateFormField('blame_conduct_exclusion_days', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Travail */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  {t.workTitle}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-orange-800 mb-3">{t.warningWork}</h4>
                    <div>
                      <label className="block text-xs text-orange-700 mb-1">{t.whenAvgBelow}</label>
                      <input
                        type="number"
                        min={0}
                        max={20}
                        step={0.5}
                        value={settingsForm.warning_work_avg_threshold ?? 8}
                        onChange={(e) => updateFormField('warning_work_avg_threshold', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-red-800 mb-3">{t.blameWork}</h4>
                    <div>
                      <label className="block text-xs text-red-700 mb-1">{t.whenAvgBelow}</label>
                      <input
                        type="number"
                        min={0}
                        max={20}
                        step={0.5}
                        value={settingsForm.blame_work_avg_threshold ?? 7}
                        onChange={(e) => updateFormField('blame_work_avg_threshold', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tableau d'honneur - Accord */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-emerald-600" />
                  {t.honorTitle} — {t.honorConditions}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <label className="block text-xs text-emerald-700 mb-1">{t.honorCompoMin}</label>
                    <input
                      type="number"
                      min={0}
                      max={20}
                      step={0.5}
                      value={settingsForm.honor_board_compo_min ?? 12}
                      onChange={(e) => updateFormField('honor_board_compo_min', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <label className="block text-xs text-emerald-700 mb-1">{t.honorAnnualMin}</label>
                    <input
                      type="number"
                      min={0}
                      max={20}
                      step={0.5}
                      value={settingsForm.honor_board_annual_min ?? 12}
                      onChange={(e) => updateFormField('honor_board_annual_min', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <label className="block text-xs text-blue-700 mb-1">{t.encouragementMin}</label>
                    <input
                      type="number"
                      min={0}
                      max={20}
                      step={0.5}
                      value={settingsForm.encouragement_min ?? 14}
                      onChange={(e) => updateFormField('encouragement_min', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <label className="block text-xs text-purple-700 mb-1">{t.felicitationsMin}</label>
                    <input
                      type="number"
                      min={0}
                      max={20}
                      step={0.5}
                      value={settingsForm.felicitations_min ?? 16}
                      onChange={(e) => updateFormField('felicitations_min', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Tableau d'honneur - Refus */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Ban className="w-5 h-5 text-red-600" />
                  {t.honorTitle} — {t.honorRefuse}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-red-50 rounded-lg p-4">
                    <label className="block text-xs text-red-700 mb-1">{t.refuseAbsence}</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.honor_refuse_absence_hours ?? 8}
                      onChange={(e) => updateFormField('honor_refuse_absence_hours', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <label className="block text-xs text-red-700 mb-1">{t.refuseExclusion}</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.honor_refuse_exclusion_days ?? 5}
                      onChange={(e) => updateFormField('honor_refuse_exclusion_days', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <label className="block text-xs text-red-700 mb-1">{t.refuseDetention}</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.honor_refuse_detention_hours ?? 15}
                      onChange={(e) => updateFormField('honor_refuse_detention_hours', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <label className="block text-xs text-red-700 mb-1">{t.refuseBlames}</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.honor_refuse_blame_count ?? 15}
                      onChange={(e) => updateFormField('honor_refuse_blame_count', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
              </div>

              {/* Classement */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Percent className="w-5 h-5 text-blue-600" />
                  {t.rankingTitle}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <label className="block text-xs text-blue-700 mb-1">{t.minSubjectsPercent}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={settingsForm.min_subjects_percentage ?? 25}
                        onChange={(e) => updateFormField('min_subjects_percentage', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-blue-600 font-medium">%</span>
                    </div>
                    <p className="text-xs text-blue-500 mt-1">{lang === 'fr' ? '0% = classer tous les élèves' : '0% = rank all students'}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <label className="block text-xs text-blue-700 mb-1">{t.minCoefsPercent}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={settingsForm.min_coefficients_percentage ?? 25}
                        onChange={(e) => updateFormField('min_coefficients_percentage', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-blue-600 font-medium">%</span>
                    </div>
                    <p className="text-xs text-blue-500 mt-1">{lang === 'fr' ? '0% = classer tous les élèves' : '0% = rank all students'}</p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="px-6 py-3 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center gap-2 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingSettings ? t.saving : t.save}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}